import { GEOLOCATION_OPTIONS } from "../GeoCameraModal";

/**
 * "granted": it worked. "denied": the browser refused. "unavailable": it neither worked nor was refused
 * (no camera, no fix, a timeout), which more settings will not fix.
 */
export type ProbeResult = "granted" | "denied" | "unavailable";

/** A real location read, for browsers that cannot report the permission state. The position itself is dropped. */
export const probeLocation = (): Promise<ProbeResult> =>
  new Promise(resolve => {
    try {
      if (!navigator.geolocation) {
        resolve("unavailable");
        return;
      }
      navigator.geolocation.getCurrentPosition(
        () => resolve("granted"),
        error => resolve(error?.code === 1 ? "denied" : "unavailable"),
        GEOLOCATION_OPTIONS
      );
    } catch {
      resolve("unavailable");
    }
  });

/** Opens the camera and closes it at once, to learn whether it is allowed. Nothing is recorded or kept. */
export const probeCamera = async (): Promise<ProbeResult> => {
  try {
    if (!navigator.mediaDevices?.getUserMedia) return "unavailable";
    const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: false });
    stream.getTracks().forEach(track => track.stop());
    return "granted";
  } catch (error) {
    const name = (error as DOMException)?.name;
    return name === "NotAllowedError" || name === "SecurityError" ? "denied" : "unavailable";
  }
};
